import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "./ui/select";
import {
  Loader2, Users, ShieldCheck, AlertTriangle, CreditCard, FileCheck,
  RefreshCw, Search, Upload, Save, UserCog, LockKeyhole, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { TIER_LABELS } from "../lib/roleAccess";

// OCI V2.22A — canonical access-tier badge styling (single source of truth).
const TIER_CLS = {
  platform_owner: "bg-slate-900 text-white border-slate-900",
  owner: "bg-indigo-600 text-white border-indigo-600",
  national_manager: "bg-violet-50 text-violet-800 border-violet-200",
  regional_manager: "bg-blue-50 text-blue-800 border-blue-200",
  campaign_manager: "bg-cyan-50 text-cyan-800 border-cyan-200",
  team_leader: "bg-amber-50 text-amber-800 border-amber-200",
  fundraiser: "bg-slate-50 text-slate-700 border-slate-200",
};

const STATUS_CLS = {
  high: "bg-rose-50 text-rose-800 border-rose-200",
  medium: "bg-amber-50 text-amber-800 border-amber-200",
  low: "bg-slate-50 text-slate-700 border-slate-200",
};

const EMP_CLS = {
  active: "bg-emerald-50 text-emerald-800 border-emerald-200",
  contractor: "bg-indigo-50 text-indigo-800 border-indigo-200",
  on_leave: "bg-amber-50 text-amber-800 border-amber-200",
  terminated: "bg-slate-100 text-slate-700 border-slate-200",
  pending: "bg-slate-50 text-slate-600 border-slate-200",
};

const RTW_CLS = {
  verified: "bg-emerald-50 text-emerald-800 border-emerald-200",
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  expired: "bg-rose-50 text-rose-800 border-rose-200",
  exempt: "bg-indigo-50 text-indigo-800 border-indigo-200",
};

const ALERT_LABEL = {
  rtw_expired: "RTW Expired",
  rtw_expiring_soon: "RTW Expiring",
  missing_bank: "Missing Bank",
  incomplete_onboarding: "Incomplete",
  inactive_still_assigned: "Inactive + Assigned",
};

export default function WorkforceTab({ isOwner = false }) {
  const [sub, setSub] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [staff, setStaff] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // staff row being edited

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [sRes, stRes, aRes] = await Promise.all([
        apiClient.get("/workforce/summary"),
        apiClient.get("/workforce/staff"),
        apiClient.get("/workforce/alerts"),
      ]);
      setSummary(sRes.data);
      setStaff(stRes.data || []);
      setAlerts(aRes.data.alerts || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load workforce data");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = staff.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (s.name || "").toLowerCase().includes(q)
      || (s.email || "").toLowerCase().includes(q)
      || (s.fullLegalName || "").toLowerCase().includes(q);
  });

  if (err) return (<Card className="border-rose-200 bg-rose-50"><CardContent className="p-4 text-sm text-rose-800" data-testid="workforce-error">{err}</CardContent></Card>);

  return (
    <div className="space-y-4" data-testid="workforce-tab">
      <Tabs value={sub} onValueChange={setSub}>
        <TabsList className="bg-white border border-slate-200 p-1 h-auto flex-wrap">
          <TabsTrigger value="overview" data-testid="workforce-sub-overview">Overview</TabsTrigger>
          <TabsTrigger value="staff" data-testid="workforce-sub-staff">Staff</TabsTrigger>
          <TabsTrigger value="compliance" data-testid="workforce-sub-compliance">Compliance</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="workforce-sub-alerts">
            Alerts {alerts.length > 0 && <span className="ml-1 text-[10px] bg-rose-600 text-white rounded-full px-1.5 py-0.5">{alerts.length}</span>}
          </TabsTrigger>
        </TabsList>

        {/* ========== OVERVIEW ========== */}
        <TabsContent value="overview" className="mt-4 space-y-4">
          {loading ? <LoadingCard/> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="workforce-overview-tiles">
                <Tile testid="wf-tile-total" label="Total Staff" value={summary?.total ?? "—"} sub={`${summary?.active ?? 0} active · ${summary?.pendingApproval ?? 0} pending`} Icon={Users} cls="border-slate-200 bg-white"/>
                <Tile testid="wf-tile-compliance" label="Compliance" value={`${summary?.compliancePct ?? 0}%`} sub={`${summary?.missingRTW ?? 0} missing RTW`} Icon={ShieldCheck} cls="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white"/>
                <Tile testid="wf-tile-expiring" label="RTW Expiring 30d" value={summary?.expiringSoon30d ?? 0} sub={`${summary?.expired ?? 0} already expired`} Icon={FileCheck} cls="border-amber-200 bg-gradient-to-br from-amber-50 to-white"/>
                <Tile testid="wf-tile-payroll" label="Missing Bank" value={summary?.missingBank ?? 0} sub={`${summary?.incompleteOnboarding ?? 0} incomplete onboarding`} Icon={CreditCard} cls="border-rose-200 bg-gradient-to-br from-rose-50 to-white"/>
              </div>
              <Card className="border-slate-200 bg-white">
                <CardContent className="p-4 text-xs text-slate-600">
                  <div className="flex items-center gap-2 mb-1"><UserCog className="h-3.5 w-3.5 text-indigo-600"/><span className="font-semibold text-slate-900">Workforce at a glance</span></div>
                  <div>{summary?.total || 0} staff on record · {summary?.active || 0} active · {summary?.pendingApproval || 0} awaiting approval · {summary?.terminated || 0} terminated.</div>
                  <div className="mt-1">{alerts.length > 0 ? <>
                    <span className="font-semibold">{alerts.length} open alert{alerts.length === 1 ? "" : "s"}</span> — see Alerts tab.
                  </> : "All clear — no compliance alerts."}</div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ========== STAFF ========== */}
        <TabsContent value="staff" className="mt-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400"/>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / email…" className="pl-9 h-9" data-testid="workforce-search"/>
            </div>
            <Button variant="ghost" size="sm" onClick={load} className="h-9" disabled={loading} data-testid="workforce-refresh">
              {loading ? <Loader2 className="h-4 w-4 animate-spin"/> : <RefreshCw className="h-4 w-4"/>}
            </Button>
          </div>
          {loading ? <LoadingCard/> : (
            <Card className="border-slate-200 bg-white"><CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50 text-xs">
                    <TableHead>Name</TableHead><TableHead>Access Tier</TableHead><TableHead>Employment</TableHead><TableHead>Phone</TableHead><TableHead>Start</TableHead><TableHead>Campaigns</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-500 py-6" data-testid="workforce-staff-empty">No staff match your search.</TableCell></TableRow>
                    ) : filtered.map(s => (
                      <TableRow key={s.id} className="text-xs hover:bg-slate-50 cursor-pointer" data-testid={`workforce-staff-row-${s.id}`} onClick={() => setEditing({ mode: "profile", id: s.id })}>
                        <TableCell>
                          <div className="font-medium text-slate-900">{s.fullLegalName || s.name}</div>
                          <div className="text-[11px] text-slate-500">{s.email}</div>
                        </TableCell>
                        <TableCell><Badge className={`border text-[10px] px-1.5 py-0 ${TIER_CLS[s.accessTier] || TIER_CLS.fundraiser}`} data-testid={`workforce-tier-${s.id}`}>{TIER_LABELS[s.accessTier] || (s.accessTier || "").replace(/_/g, " ")}</Badge></TableCell>
                        <TableCell><Badge className={`border text-[10px] px-1.5 py-0 ${EMP_CLS[s.employmentStatus] || EMP_CLS.pending}`}>{(s.employmentStatus || "pending").replace(/_/g, " ")}</Badge></TableCell>
                        <TableCell className="font-mono text-[11px]">{s.phone || "—"}</TableCell>
                        <TableCell className="text-[11px] text-slate-600">{s.startDate ? new Date(s.startDate).toLocaleDateString("en-GB") : "—"}</TableCell>
                        <TableCell className="text-[11px] text-slate-600">{(s.assignedCampaigns || []).length}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEditing({ mode: "profile", id: s.id }); }} className="h-7 px-2 text-[11px]" data-testid={`workforce-edit-profile-${s.id}`}>
                            <UserCog className="h-3.5 w-3.5 mr-1"/>Profile
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent></Card>
          )}
        </TabsContent>

        {/* ========== COMPLIANCE ========== */}
        <TabsContent value="compliance" className="mt-4 space-y-3">
          {loading ? <LoadingCard/> : (
            <Card className="border-slate-200 bg-white"><CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50 text-xs">
                    <TableHead>Name</TableHead><TableHead>Right to Work</TableHead><TableHead>RTW Expiry</TableHead><TableHead>ID Doc</TableHead><TableHead>Proof of Address</TableHead>
                    {isOwner && <TableHead>NI</TableHead>}
                    {isOwner && <TableHead>Bank</TableHead>}
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {filtered.map(s => {
                      const c = s.compliance || {};
                      return (
                        <TableRow key={s.id} className="text-xs" data-testid={`workforce-compliance-row-${s.id}`}>
                          <TableCell className="font-medium">{s.fullLegalName || s.name}</TableCell>
                          <TableCell><Badge className={`border text-[10px] px-1.5 py-0 ${RTW_CLS[c.rightToWorkStatus] || RTW_CLS.pending}`}>{c.rightToWorkStatus}</Badge></TableCell>
                          <TableCell className="text-[11px]">{c.rightToWorkExpiry ? new Date(c.rightToWorkExpiry).toLocaleDateString("en-GB") : "—"}{typeof c.rightToWorkDaysUntilExpiry === "number" && c.rightToWorkDaysUntilExpiry <= 30 && <div className={`text-[10px] ${c.rightToWorkDaysUntilExpiry < 0 ? "text-rose-700" : "text-amber-700"}`}>{c.rightToWorkDaysUntilExpiry < 0 ? `expired ${Math.abs(c.rightToWorkDaysUntilExpiry)}d ago` : `${c.rightToWorkDaysUntilExpiry}d left`}</div>}</TableCell>
                          <TableCell>{c.idDocumentUrl ? <a href={c.idDocumentUrl} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline text-[11px]" data-testid={`wf-id-link-${s.id}`}>View</a> : <span className="text-slate-400 text-[11px]">—</span>}</TableCell>
                          <TableCell>{c.proofOfAddressUrl ? <a href={c.proofOfAddressUrl} target="_blank" rel="noreferrer" className="text-indigo-700 hover:underline text-[11px]" data-testid={`wf-poa-link-${s.id}`}>View</a> : <span className="text-slate-400 text-[11px]">—</span>}</TableCell>
                          {isOwner && <TableCell className="font-mono text-[11px]">{c.niNumberMasked || "—"}</TableCell>}
                          {isOwner && <TableCell className="font-mono text-[11px]">{c.bankAccountNumberMasked ? `${c.bankSortCodeMasked || "••-••-••"} · ${c.bankAccountNumberMasked}` : <span className="text-rose-700">Missing</span>}</TableCell>}
                          <TableCell className="text-right">
                            {isOwner ? (
                              <Button variant="ghost" size="sm" onClick={() => setEditing({ mode: "compliance", id: s.id })} className="h-7 px-2 text-[11px]" data-testid={`workforce-edit-compliance-${s.id}`}>
                                <LockKeyhole className="h-3.5 w-3.5 mr-1"/>Edit
                              </Button>
                            ) : <span className="text-[10px] text-slate-400">Owner only</span>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent></Card>
          )}
        </TabsContent>

        {/* ========== ALERTS ========== */}
        <TabsContent value="alerts" className="mt-4 space-y-3">
          {loading ? <LoadingCard/> : alerts.length === 0 ? (
            <Card className="border-emerald-200 bg-emerald-50"><CardContent className="p-4 text-sm text-emerald-900" data-testid="workforce-alerts-empty">
              No workforce alerts. All compliance is clean.
            </CardContent></Card>
          ) : (
            <div className="space-y-2" data-testid="workforce-alerts-list">
              {alerts.map((a, i) => (
                <Card key={`${a.userId}-${a.type}-${i}`} className="border-slate-200 bg-white" data-testid={`workforce-alert-${a.userId}-${a.type}`}>
                  <CardContent className="p-3 flex items-start gap-3">
                    <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${a.severity === "high" ? "text-rose-600" : a.severity === "medium" ? "text-amber-600" : "text-slate-500"}`}/>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-slate-900">{a.name}</span>
                        <Badge className={`border text-[10px] px-1.5 py-0 ${STATUS_CLS[a.severity]}`}>{ALERT_LABEL[a.type] || a.type}</Badge>
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{a.severity}</span>
                      </div>
                      <div className="text-[11px] text-slate-600 mt-0.5">{a.detail}</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setSub("staff"); setEditing({ mode: a.type === "missing_bank" || a.type === "rtw_expired" || a.type === "rtw_expiring_soon" ? "compliance" : "profile", id: a.userId }); }} className="h-7 px-2 text-[11px]" data-testid={`wf-alert-fix-${a.userId}-${a.type}`}>Fix</Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {editing && <WorkforceStaffDialog editing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} isOwner={isOwner} assignableTiers={summary?.assignableTiers || []}/>}
    </div>
  );
}

function Tile({ testid, label, value, sub, Icon, cls }) {
  return (
    <Card className={`p-3 border ${cls}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1"><Icon className="h-4 w-4 text-slate-700"/><div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">{label}</div></div>
      <div className="font-heading font-black text-2xl text-slate-900">{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </Card>
  );
}

function LoadingCard() {
  return <Card className="p-4 border-slate-200" data-testid="workforce-loading"><div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin"/>Loading workforce…</div></Card>;
}

function WorkforceStaffDialog({ editing, onClose, onSaved, isOwner, assignableTiers = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [ecName, setEcName] = useState("");
  const [ecPhone, setEcPhone] = useState("");
  const [ecRel, setEcRel] = useState("");
  const [tier, setTier] = useState("");
  const [savingTier, setSavingTier] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`/workforce/staff/${editing.id}`);
        const d = res.data;
        setData(d);
        setTier(d.accessTier || "");
        if (editing.mode === "profile") {
          setForm({
            fullLegalName: d.fullLegalName || "", phone: d.phone || "",
            dob: d.dob || "", address: d.address || "",
            startDate: d.startDate || "", employmentStatus: d.employmentStatus || "active",
            preferredOperatingAreas: (d.preferredOperatingAreas || []).join(", "),
            notes: d.notes || "",
          });
          const ec = d.emergencyContact || {};
          setEcName(ec.name || ""); setEcPhone(ec.phone || ""); setEcRel(ec.relationship || "");
        } else {
          setForm({
            rightToWorkStatus: d.compliance?.rightToWorkStatus || "pending",
            rightToWorkExpiry: d.compliance?.rightToWorkExpiry || "",
            niNumber: "", payrollRef: d.compliance?.payrollRef || "",
            bankSortCode: "", bankAccountNumber: "",
          });
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load staff record");
        onClose();
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing.id, editing.mode]);

  const save = async () => {
    setSaving(true);
    try {
      if (editing.mode === "profile") {
        const payload = { ...form };
        payload.preferredOperatingAreas = (form.preferredOperatingAreas || "").split(",").map(s => s.trim()).filter(Boolean);
        if (ecName || ecPhone || ecRel) payload.emergencyContact = { name: ecName, phone: ecPhone, relationship: ecRel };
        await apiClient.put(`/workforce/staff/${editing.id}`, payload);
        toast.success("Profile saved.");
      } else {
        const payload = {};
        Object.keys(form).forEach(k => { if (form[k] !== "" && form[k] !== undefined) payload[k] = form[k]; });
        await apiClient.put(`/workforce/staff/${editing.id}/compliance`, payload);
        toast.success("Compliance saved.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const saveTier = async () => {
    if (!tier || tier === data?.accessTier) return;
    setSavingTier(true);
    try {
      const res = await apiClient.put(`/workforce/${editing.id}/access-tier`, { tier });
      toast.success(`Access tier updated to ${TIER_LABELS[res.data.accessTier] || res.data.accessTier}.`);
      setData(d => ({ ...d, accessTier: res.data.accessTier, role: res.data.role, teamRole: res.data.teamRole, managerLevel: res.data.managerLevel }));
      onSaved(); // reloads parent list → refreshes canonical badge immediately
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update access tier");
    } finally { setSavingTier(false); }
  };

  const uploadDoc = async (docType, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await apiClient.post(`/workforce/staff/${editing.id}/upload/${docType}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${docType === "id" ? "ID" : "Proof of address"} uploaded`);
      setData(d => ({ ...d, compliance: { ...d.compliance, [docType === "id" ? "idDocumentUrl" : "proofOfAddressUrl"]: res.data.url } }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="workforce-dialog">
        <DialogHeader>
          <DialogTitle>{editing.mode === "profile" ? "Edit Profile" : "Edit Compliance"} · {data?.name || ""}</DialogTitle>
        </DialogHeader>
        {loading ? <div className="py-8 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400"/></div> : (
          editing.mode === "profile" ? (
            <div className="space-y-3">
              {/* OCI V2.22A — Role & Access (single canonical promote/demote surface) */}
              <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-3" data-testid="wf-role-access-section">
                <div className="flex items-center gap-1.5 mb-2">
                  <ShieldCheck className="h-3.5 w-3.5 text-indigo-600"/>
                  <span className="text-[10px] uppercase tracking-wider font-semibold text-indigo-900">Role &amp; Access</span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-slate-600">Current tier:</span>
                  <Badge className={`border text-[10px] px-1.5 py-0 ${TIER_CLS[data?.accessTier] || TIER_CLS.fundraiser}`} data-testid="wf-current-tier">{TIER_LABELS[data?.accessTier] || (data?.accessTier || "").replace(/_/g, " ")}</Badge>
                </div>
                {data?.role === "fundraiser" && data?.accessTier !== "fundraiser" && (
                  <div className="text-[10px] text-slate-500 mb-2" data-testid="wf-secondary-role">Secondary context: fundraiser account (self-service active)</div>
                )}
                {assignableTiers.length === 0 ? (
                  <div className="text-[11px] text-slate-500" data-testid="wf-role-access-readonly">You do not have authority to change access levels.</div>
                ) : !assignableTiers.includes(data?.accessTier) ? (
                  <div className="text-[11px] text-amber-700" data-testid="wf-role-access-outofscope">This person's current access level is outside your authority — you cannot change it.</div>
                ) : (
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <Label className="text-xs text-slate-600">Set access tier</Label>
                      <Select value={tier} onValueChange={setTier}>
                        <SelectTrigger className="h-9" data-testid="wf-tier-select"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          {assignableTiers.map(t => (
                            <SelectItem key={t} value={t} data-testid={`wf-tier-option-${t}`}>{TIER_LABELS[t] || t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={saveTier} disabled={savingTier || !tier || tier === data?.accessTier} className="h-9 bg-indigo-600 hover:bg-indigo-700" data-testid="wf-tier-save">
                      {savingTier ? <Loader2 className="h-4 w-4 animate-spin"/> : "Apply"}
                    </Button>
                  </div>
                )}
              </div>
              <Field label="Full Legal Name" value={form.fullLegalName} onChange={(v) => setForm({ ...form, fullLegalName: v })} testid="wf-edit-fullLegalName"/>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="wf-edit-phone"/>
                <Field label="DOB" type="date" value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} testid="wf-edit-dob"/>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Address</Label>
                <Textarea value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} data-testid="wf-edit-address"/>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Preferred Operating Areas</Label>
                <Input value={form.preferredOperatingAreas || ""} onChange={(e) => setForm({ ...form, preferredOperatingAreas: e.target.value })} placeholder="e.g. Peterborough, Cambridge, Hounslow" data-testid="wf-edit-preferred-areas"/>
                <p className="text-[10px] text-slate-400 mt-1">Comma-separated towns/areas. Soft scheduling signal only — not a hard restriction, not a map.</p>
              </div>
              <div className="rounded-md border border-slate-200 p-3 bg-slate-50">
                <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-2">Emergency Contact</div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Name" value={ecName} onChange={setEcName} testid="wf-edit-ec-name"/>
                  <Field label="Phone" value={ecPhone} onChange={setEcPhone} testid="wf-edit-ec-phone"/>
                </div>
                <div className="mt-2"><Field label="Relationship" value={ecRel} onChange={setEcRel} testid="wf-edit-ec-rel"/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Start Date" type="date" value={form.startDate} onChange={(v) => setForm({ ...form, startDate: v })} testid="wf-edit-startDate"/>
                <div>
                  <Label className="text-xs text-slate-600">Employment Status</Label>
                  <Select value={form.employmentStatus} onValueChange={(v) => setForm({ ...form, employmentStatus: v })}>
                    <SelectTrigger className="h-9" data-testid="wf-edit-employmentStatus"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="contractor">Contractor</SelectItem>
                      <SelectItem value="on_leave">On Leave</SelectItem>
                      <SelectItem value="terminated">Terminated</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Notes</Label>
                <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="wf-edit-notes"/>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-600">Right to Work Status</Label>
                <Select value={form.rightToWorkStatus} onValueChange={(v) => setForm({ ...form, rightToWorkStatus: v })}>
                  <SelectTrigger className="h-9" data-testid="wf-edit-rtwStatus"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="exempt">Exempt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="RTW Expiry" type="date" value={form.rightToWorkExpiry} onChange={(v) => setForm({ ...form, rightToWorkExpiry: v })} testid="wf-edit-rtwExpiry"/>
              <div className="grid grid-cols-2 gap-2">
                <Field label="NI Number (overwrites)" value={form.niNumber} onChange={(v) => setForm({ ...form, niNumber: v })} testid="wf-edit-ni" placeholder="AB 12 34 56 C"/>
                <Field label="Payroll Ref" value={form.payrollRef} onChange={(v) => setForm({ ...form, payrollRef: v })} testid="wf-edit-payrollRef"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Sort Code (overwrites)" value={form.bankSortCode} onChange={(v) => setForm({ ...form, bankSortCode: v })} testid="wf-edit-sortCode" placeholder="12-34-56"/>
                <Field label="Account (overwrites)" value={form.bankAccountNumber} onChange={(v) => setForm({ ...form, bankAccountNumber: v })} testid="wf-edit-accountNumber" placeholder="12345678"/>
              </div>
              {data?.compliance?.hasBankDetails && <div className="text-[11px] text-slate-500">Current: {data.compliance.bankSortCodeMasked} · {data.compliance.bankAccountNumberMasked}</div>}
              {isOwner && (
                <div className="rounded-md border border-slate-200 p-3 bg-slate-50 space-y-2">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600">Document Uploads</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <UploadRow label="ID Document" current={data?.compliance?.idDocumentUrl} onUpload={(f) => uploadDoc("id", f)} testid="wf-upload-id"/>
                    <UploadRow label="Proof of Address" current={data?.compliance?.proofOfAddressUrl} onUpload={(f) => uploadDoc("address", f)} testid="wf-upload-poa"/>
                  </div>
                </div>
              )}
            </div>
          )
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="workforce-dialog-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || loading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="workforce-dialog-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <Save className="h-4 w-4 mr-1"/>}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <Label className="text-xs text-slate-600">{label}</Label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="h-9" data-testid={testid} placeholder={placeholder}/>
    </div>
  );
}

function UploadRow({ label, current, onUpload, testid }) {
  return (
    <div>
      <div className="text-[11px] font-semibold text-slate-700 mb-1">{label}</div>
      {current && <a href={current} target="_blank" rel="noreferrer" className="block mb-1 text-[11px] text-indigo-700 hover:underline"><FileText className="inline h-3 w-3 mr-1"/>View current</a>}
      <label className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-slate-200 bg-white text-xs cursor-pointer hover:bg-slate-50" data-testid={testid}>
        <Upload className="h-3.5 w-3.5"/>Upload
        <input type="file" className="hidden" accept="image/png,image/jpeg,image/webp,application/pdf" onChange={(e) => onUpload(e.target.files?.[0])}/>
      </label>
    </div>
  );
}
